import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { QcChartPoint, QcChartSummary } from '../../services/qcService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
);

/**
 * Generic Levey-Jennings chart used for any single QC chart series.
 * - Solid green line: measured values
 * - Solid grey: mean (central line)
 * - Dashed orange: ±2σ warning limits
 * - Dashed red:    ±3σ control limits
 * - Per-point color: green (in control), orange (warning), red (out of control)
 */

export interface LeveyJenningsChartProps {
  summary: QcChartSummary | null;
  points: QcChartPoint[];
  height?: number;
  // Optional override for the y-axis title — useful when reusing for RPD / Blank.
  yLabel?: string;
  // Optional override for the central reference (e.g. certified value for MRC,
  // 0 for blanks, 0 for RPD). Defaults to summary.baseline_mean.
  centerOverride?: number;
  certifiedValue?: number | null;
  certifiedUnit?: string | null;
}

export const LeveyJenningsChart: React.FC<LeveyJenningsChartProps> = ({
  summary,
  points,
  height = 320,
  yLabel,
  centerOverride,
  certifiedValue,
  certifiedUnit,
}) => {
  const useCertified = typeof certifiedValue === 'number' && certifiedValue > 0;

  const data = useMemo(() => {
    const labels = points.map((p) =>
      new Date(p.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    );
    const toDisplay = (v: number) => (useCertified ? (v / 100) * certifiedValue! : v);
    const values = points.map((p) => toDisplay(Number(p.value)));
    const rawMean = centerOverride ?? summary?.baseline_mean ?? 0;
    const mean = useCertified ? certifiedValue! : rawMean;
    const ucl = useCertified ? toDisplay(summary?.ucl ?? 120) : (summary?.ucl ?? mean);
    const lcl = useCertified ? toDisplay(summary?.lcl ?? 80) : (summary?.lcl ?? mean);
    const uwl = useCertified ? toDisplay(summary?.uwl ?? 110) : (summary?.uwl ?? mean);
    const lwl = useCertified ? toDisplay(summary?.lwl ?? 90) : (summary?.lwl ?? mean);

    const pointBgColors = points.map((p) => {
      if (p.out_of_control) return '#d32f2f';
      if (p.warning_level) return '#ef6c00';
      return '#2e7d32';
    });

    return {
      labels,
      datasets: [
        {
          label: 'Valeur mesurée',
          data: values,
          borderColor: '#1565c0',
          backgroundColor: 'rgba(21, 101, 192, 0.12)',
          pointBackgroundColor: pointBgColors,
          pointBorderColor: pointBgColors,
          tension: 0.18,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Moyenne (central)',
          data: labels.map(() => mean),
          borderColor: '#616161',
          borderDash: [],
          pointRadius: 0,
          fill: false,
        },
        {
          label: '+2σ (UWL)',
          data: labels.map(() => uwl),
          borderColor: '#ef6c00',
          borderDash: [6, 4],
          pointRadius: 0,
          fill: false,
        },
        {
          label: '-2σ (LWL)',
          data: labels.map(() => lwl),
          borderColor: '#ef6c00',
          borderDash: [6, 4],
          pointRadius: 0,
          fill: false,
        },
        {
          label: '+3σ (UCL)',
          data: labels.map(() => ucl),
          borderColor: '#d32f2f',
          borderDash: [3, 3],
          pointRadius: 0,
          fill: false,
        },
        {
          label: '-3σ (LCL)',
          data: labels.map(() => lcl),
          borderColor: '#d32f2f',
          borderDash: [3, 3],
          pointRadius: 0,
          fill: false,
        },
      ],
    };
  }, [points, summary, centerOverride, certifiedValue, useCertified]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { position: 'bottom' as const, labels: { boxWidth: 12 } },
        tooltip: {
          callbacks: {
            afterBody: (items: any[]) => {
              const idx = items?.[0]?.dataIndex;
              if (idx === undefined) return '';
              const p = points[idx];
              if (!p) return '';
              const pieces: string[] = [];
              if (p.batch_id) pieces.push(`Lot: ${p.batch_id}`);
              if (p.sample_id) pieces.push(`Echantillon: ${p.sample_id}`);
              const violations = Object.entries(p.rule_violations || {})
                .filter(([, v]) => v)
                .map(([k]) => k);
              if (violations.length > 0) pieces.push(`Westgard: ${violations.join(', ')}`);
              return pieces.join('\n');
            },
          },
        },
        title: { display: false },
      },
      scales: {
        y: {
          title: { display: true, text: yLabel || (useCertified && certifiedUnit ? `Valeur (${certifiedUnit})` : 'Valeur') },
        },
        x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
      },
    }),
    [points, yLabel, useCertified, certifiedUnit],
  );

  return (
    <div style={{ height }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default LeveyJenningsChart;
